import React from 'react';
import clsx from 'clsx';
import { ERROR_TYPE, MINTING_ERROR_ACTIONS, PILLARS } from '@soundxyz/utils/constants/logging';
import { Box } from '@components/basics/Box';
import { Spinner } from '@components/basics/Spinner';
import { Modal } from '@components/main-components/modals/Modal';
import { List, Tabs, Trigger } from '@components/main-components/tabs/Tab';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { ModalContext } from '@context/modal';
import { useLogError } from '@hooks/useLogErrorNew';
import { AVAILABLE_CHAIN_IDS, ONRAMPER_KEY } from '@utils/constants';
import { BrigdeOptimism } from './BridgeOptimism';

const isArtistOnlyPage = false;
const pillar = PILLARS.MINTING;

export const OnRampModal = () => {
  const { authUserAddress } = useAuth();
  const { activeChainId } = useConnectedAuth();
  const { closeCancelModal } = ModalContext.useContainer();

  const logError = useLogError();

  const [iFrameLoading, setiFrameLoading] = React.useState(true);

  const [tabValue, setTabValue] = React.useState<'card' | 'bridge'>('card');

  const isOptimism = activeChainId.current === AVAILABLE_CHAIN_IDS.optimism;
  const showTabs = isOptimism;

  const iframeUrl = React.useMemo(() => {
    const url = new URL('https://buy.onramper.com');

    if (!ONRAMPER_KEY) throw new Error('ONRAMPER_KEY is not defined');

    url.searchParams.set('apiKey', ONRAMPER_KEY);
    if (isOptimism) {
      url.searchParams.set('defaultCrypto', 'ETH_OPTIMISM');
    } else {
      url.searchParams.set('defaultCrypto', 'ETH');
    }
    url.searchParams.set('onlyCryptos', 'ETH,ETH_OPTIMISM');
    url.searchParams.set('theme', 'light');
    if (authUserAddress) {
      url.searchParams.set('wallets', `ETH:${authUserAddress}`);
    }
    return url.toString();
  }, [authUserAddress, isOptimism]);

  const sharedTriggerClass =
    'w-full data-[state=active]:bg-black data-[state=active]:text-white data-[state=active]:border-black border border-base300 bg-white text-black py-2';

  const newBody = (
    <div className="flex h-full w-full items-center justify-center overflow-hidden">
      <Tabs defaultValue="card" className="h-full w-full">
        <List className="z-above2 flex w-full items-center justify-center px-4">
          <Trigger
            className={clsx(sharedTriggerClass, 'rounded-l-md')}
            value="card"
            onClick={() => {
              setTabValue('card');
            }}
          >
            Via Card
          </Trigger>
          <Trigger
            value="bridge"
            className={clsx(sharedTriggerClass, 'rounded-r-md')}
            onClick={() => {
              setTabValue('bridge');
            }}
          >
            Bridge ETH
          </Trigger>
        </List>
        <div
          className={clsx('"w-full h-[98%] md:h-[95%] ', tabValue === 'card' ? 'block' : 'hidden')}
        >
          {iFrameLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <Spinner />
            </div>
          )}

          <Box
            as="iframe"
            className={clsx(iFrameLoading ? 'opacity-0' : 'opacity-100', 'transition-opacity')}
            height="100%"
            width="100%"
            src={iframeUrl}
            onLoad={() => setiFrameLoading(false)}
            onError={() => {
              const message = 'Error loading the onramp modal';
              logError({
                action: MINTING_ERROR_ACTIONS.ONRAMP_MODAL_ERRROR,
                error: new Error(message),
                errorType: ERROR_TYPE.IFRAME_ERROR,
                message,
                pillar,
                isArtistOnlyPage,
                level: 'error',
                indexedTags: {
                  authUserAddress,
                  activeChainId: activeChainId.current,
                },
              });
            }}
          />
        </div>

        <div
          className={clsx(
            'flex h-full w-full justify-center px-4',
            tabValue === 'bridge' ? 'block' : 'hidden',
          )}
        >
          <BrigdeOptimism />
        </div>
      </Tabs>
    </div>
  );

  const body = (
    <div className="h-full w-full md:h-[99%]">
      {iFrameLoading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <Spinner />
        </div>
      )}

      <Box
        as="iframe"
        height="100%"
        width="100%"
        src={iframeUrl}
        onLoad={() => setiFrameLoading(false)}
        className={clsx(iFrameLoading ? 'opacity-0' : 'opacity-100', 'transition-opacity')}
      />
    </div>
  );

  return (
    <Modal
      body={showTabs ? newBody : body}
      header={<h2 className="items-center">Add Funds</h2>}
      bodyStyles={{
        width: 344,
        height: showTabs ? 460 : 420,
        position: 'relative',
        right: 16,
        '@tablet': {
          width: 400,
          height: showTabs ? 600 : 570,
          margin: '0 auto',
        },
      }}
      containerStyles={{
        paddingBottom: 4,
      }}
      closeModal={closeCancelModal}
    />
  );
};
